// The launch card AgentX prepares (optimization_card). The PERSON edits and launches it — the agent cannot: Launch
// posts to /api/agentx/launch/, which re-builds and re-validates the card server-side before calling the platform's
// existing batch launcher. Values are typed as comma lists ("5, 10, 15"; "None" = switched off; true / false).
import React, { useMemo, useState } from 'react';
import agentxService from '../../services/agentxService';

const METRICS = ['Sharpe Ratio', 'Blended Sharpe', 'Return', 'Blended Return', 'Profit Factor', 'Win Rate'];
const SPLITS = [10, 20, 30];

const fmtVals = (vals) => (Array.isArray(vals) ? vals.map((v) => (v === null ? 'None' : String(v))).join(', ') : String(vals));
const parseVals = (text) => String(text).split(',').map((x) => x.trim()).filter(Boolean).map((x) => {
  if (/^none$/i.test(x)) return null;
  if (/^true$/i.test(x)) return true;
  if (/^false$/i.test(x)) return false;
  const n = Number(x);
  return Number.isFinite(n) ? n : x;
});

export default function LaunchCard({ initial }) {
  const [card, setCard] = useState(initial);
  const [edits, setEdits] = useState(() => Object.fromEntries(
    Object.entries(initial.param_ranges || {}).map(([k, v]) => [k, fmtVals(v)])));
  const [win, setWin] = useState({
    start: initial.window?.start || '', oos_end: initial.window?.oos_end || '',
    oos_split_pct: initial.window?.oos_split_pct || 10, maximize: initial.maximize || 'Sharpe Ratio',
    idle_deployment: initial.idle_deployment || 'cash',
  });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [launched, setLaunched] = useState(null);

  const changes = useMemo(() => {
    const pr = {};
    Object.entries(edits).forEach(([k, txt]) => {
      const vals = parseVals(txt);
      if (JSON.stringify(vals) !== JSON.stringify(initial.param_ranges?.[k])) pr[k] = vals;
    });
    return { param_ranges: pr, start: win.start, oos_end: win.oos_end, oos_split_pct: Number(win.oos_split_pct),
             maximize: win.maximize, idle_deployment: win.idle_deployment };
  }, [edits, win, initial]);

  const body = () => ({ symbol: card.ticker, strategy: card.strategy, changes });

  const check = async () => {
    setBusy('check'); setMsg('');
    try { setCard((await agentxService.checkCard(body())).data); } catch { setMsg('Could not check the card.'); }
    setBusy('');
  };
  const launch = async () => {
    setBusy('launch'); setMsg('');
    try {
      const r = await agentxService.launch(body());
      setLaunched(r.data);
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.card) setCard(d.card);
      setMsg((d.reasons || [d.error || 'The launch failed.']).join(' · '));
    }
    setBusy('');
  };

  const ready = card.readiness || {};
  return (
    <div className="mt-3 rounded-lg border border-brand-gold/30 bg-brand-gold/[0.04] p-3 text-xs text-gray-700 dark:text-gray-300">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-serif text-sm text-gray-900 dark:text-gray-100">{card.strategy} · {card.ticker}</span>
        <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${card.settings_class === 'production'
          ? 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400' : 'border-amber-500/40 text-amber-600 dark:text-amber-400'}`}>
          {card.settings_class === 'production' ? 'your production settings' : 'new settings class'}
        </span>
        <span className="ml-auto tabular-nums text-gray-500">{(card.combinations || 0).toLocaleString()} combinations</span>
      </div>

      <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
        <label className="flex flex-col gap-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-400">Start</span>
          <input value={win.start} onChange={(e) => setWin({ ...win, start: e.target.value })} disabled={!!launched}
                 className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1" /></label>
        <label className="flex flex-col gap-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-400">End</span>
          <input value={win.oos_end} onChange={(e) => setWin({ ...win, oos_end: e.target.value })} disabled={!!launched}
                 className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1" /></label>
        <label className="flex flex-col gap-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-400">Out-of-sample</span>
          <select value={win.oos_split_pct} onChange={(e) => setWin({ ...win, oos_split_pct: e.target.value })} disabled={!!launched}
                  className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1">
            {SPLITS.map((s) => <option key={s} value={s}>{s}%</option>)}</select></label>
        <label className="flex flex-col gap-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-400">Maximize</span>
          <select value={win.maximize} onChange={(e) => setWin({ ...win, maximize: e.target.value })} disabled={!!launched}
                  className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1">
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        <label className="flex flex-col gap-0.5"><span className="uppercase tracking-wider text-[10px] text-gray-400">Idle money</span>
          <input value={win.idle_deployment} onChange={(e) => setWin({ ...win, idle_deployment: e.target.value })} disabled={!!launched}
                 className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1" /></label>
      </div>

      <details className="mt-2">
        <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-gray-500 hover:text-brand-gold">
          Parameter ranges ({Object.keys(edits).length}){card.changed_params?.length ? ` · changed: ${card.changed_params.join(', ')}` : ''}
        </summary>
        <div className="mt-1.5 space-y-1">
          {Object.entries(edits).map(([k, txt]) => (
            <label key={k} className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
              <span className="sm:w-44 shrink-0 font-mono text-[11px] text-gray-500">{k}</span>
              <input value={txt} onChange={(e) => setEdits({ ...edits, [k]: e.target.value })} disabled={!!launched}
                     className="flex-1 min-w-0 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117] px-1.5 py-1 font-mono" />
            </label>
          ))}
        </div>
      </details>

      <div className="mt-2 space-y-0.5">
        {card.estimate && <p className="tabular-nums">About {card.estimate.minutes} min, ~${card.estimate.cost_usd} of spot time.</p>}
        {(card.notes || []).map((n, i) => <p key={i} className="text-amber-600 dark:text-amber-400">{n}</p>)}
        {(card.errors || []).map((e, i) => <p key={i} className="text-red-500">{e}</p>)}
        {card.reuse && <p className="text-gray-500">An identical run already exists: #{card.reuse.run_id} ({card.reuse.status}).</p>}
        {card.ok && !card.reuse && ready.ready === false && <p className="text-red-500">Not ready: {(ready.problems || []).join('; ')}</p>}
        {msg && <p className="text-red-500">{msg}</p>}
        {launched && (
          <p className="text-emerald-600 dark:text-emerald-400">
            Launched run #{launched.run_id} (batch #{launched.batch_id}). Ask AgentX “status of run {launched.run_id}”.
          </p>
        )}
      </div>

      {!launched && (
        <div className="mt-2 flex gap-2 justify-end">
          <button type="button" onClick={check} disabled={!!busy}
                  className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 hover:border-brand-gold disabled:opacity-40">
            {busy === 'check' ? 'Checking…' : 'Check'}
          </button>
          <button type="button" onClick={launch} disabled={!!busy || !card.launchable}
                  className="px-3 py-1.5 rounded bg-brand-gold text-black font-medium disabled:opacity-40">
            {busy === 'launch' ? 'Launching…' : 'Launch'}
          </button>
        </div>
      )}
    </div>
  );
}
